import { ArrowLeft, Palette, Heart, Sparkles, Clock, Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function VisualArtGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-purple-700 hover:text-purple-800 hover:bg-purple-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-purple-100 rounded-full">
              <Palette className="w-8 h-8 text-purple-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Visual Art Session</h1>
              <p className="text-purple-600">Week 11, Day 2 - Creative Expression</p>
            </div>
          </div>

          <div className="prose prose-purple max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Today, set a timer for 30 minutes and create something visual. Remember: the goal is the 
              process, not the product. There's no wrong way to do this!
            </p>

            <div className="bg-purple-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-purple-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Why Visual Art Heals
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Reduces cortisol (stress hormone) by 45%</li>
                <li>• Activates the brain's reward center</li>
                <li>• Provides a meditative, flow state</li>
                <li>• Allows emotional expression without words</li>
                <li>• Builds creative confidence</li>
                <li>• No experience required - everyone can create</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Choose Your Activity</h2>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-white border-2 border-purple-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-2">Paint or Draw</h3>
                <p className="text-gray-600 text-sm">Watercolors, acrylics, colored pencils, markers - whatever you have.</p>
              </div>
              
              <div className="bg-white border-2 border-purple-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-2">Adult Coloring</h3>
                <p className="text-gray-600 text-sm">Relaxing, meditative, and requires no artistic skill.</p>
              </div>
              
              <div className="bg-white border-2 border-purple-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-2">Collage Making</h3>
                <p className="text-gray-600 text-sm">Cut and arrange images from magazines into something new.</p>
              </div>
              
              <div className="bg-white border-2 border-purple-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-2">Doodling</h3>
                <p className="text-gray-600 text-sm">Free-form shapes, patterns, and designs. Let your pen wander.</p>
              </div>
              
              <div className="bg-white border-2 border-purple-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-2">Digital Art</h3>
                <p className="text-gray-600 text-sm">Use apps like Procreate, Canva, or free drawing apps.</p>
              </div>
              
              <div className="bg-white border-2 border-purple-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-2">Zentangle</h3>
                <p className="text-gray-600 text-sm">Structured patterns that are both calming and beautiful.</p>
              </div>
            </div>

            <div className="bg-amber-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5" />
                Prompts to Get Started
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Draw or paint how you're feeling right now (abstract colors/shapes welcome)</li>
                <li>• Create something inspired by your favorite season</li>
                <li>• Fill a page with patterns and shapes</li>
                <li>• Paint/draw a memory that makes you happy</li>
                <li>• Create without lifting your pen/brush from the page</li>
                <li>• Close your eyes and draw for the first minute</li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-purple-800 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Your 30-Minute Session
              </h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>Minutes 1-5:</strong> Gather materials. Take three deep breaths. Set your intention to enjoy the process.</p>
                <p><strong>Minutes 5-25:</strong> Create freely. Don't judge what appears. If your inner critic speaks, thank it and keep going.</p>
                <p><strong>Minutes 25-30:</strong> Step back and observe. Notice how you feel. Appreciate that you made something.</p>
              </div>
            </div>

            <div className="bg-rose-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-rose-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Release Perfectionism
              </h2>
              <p className="text-gray-700 mb-4">Remember these truths:</p>
              <ul className="space-y-2 text-gray-700">
                <li>• There is no "bad" art when the goal is expression</li>
                <li>• Children create freely - channel that energy</li>
                <li>• The process is the point, not the product</li>
                <li>• You don't need to show anyone</li>
                <li>• Even "mistakes" can become the best parts</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
